import { FC, useEffect } from 'react';

import { Currency } from '@/shared/api';
import { EQueryKeys } from '@/shared/config';
import { Select, SingleSelectValue, Skeleton } from '@/shared/ui';

import { paginationCurrenciesQuery } from '../../api';
import { serializeCurrenciesSelect } from '../../lib';
import { useCurrenciesStore } from '../../model';
import { ICurrency } from '../../types';
import { CurrencyIcon } from '../currency-icon';
import { TCurrenciesSelect } from './types';

export const CurrenciesSelect: FC<TCurrenciesSelect> = ({
  value,
  onSelect,
  defaultCurrency,
  placeholder,
  ...props
}) => {
  const currencies = useCurrenciesStore((state) => state.currenciesById);

  const selectCurrencyHandler = (currency: SingleSelectValue<ICurrency>) => {
    onSelect(currency?.option ?? null);
  };

  useEffect(() => {
    if (!defaultCurrency || !currencies.size) return;
    const currency = currencies.get(defaultCurrency);
    if (!currency) return;
    onSelect(currency);
  }, [defaultCurrency, currencies, onSelect]);

  return (
    <Select<ICurrency>
      value={
        value
          ? {
              value: value.id,
              label: value.name ?? value.ticker,
              option: value,
            }
          : null
      }
      onSelect={selectCurrencyHandler}
      queryProps={{
        queryKey: EQueryKeys.CurrenciesQueryKey,
        queryFn: paginationCurrenciesQuery,
        serialize: (currencies) =>
          serializeCurrenciesSelect(currencies as Currency[]),
      }}
      modalProps={{
        classNames: {
          wrapper: 'max-h-[min(567px,90dvh)] h-dvh',
        },
      }}
      placeholder={placeholder}
      renderOptionItem={(currency) => (
        <div className="flex w-full items-center gap-4">
          <CurrencyIcon
            ticker={currency.option?.ticker}
            className="h-[34px] w-[34px]"
          />
          <p className="typography-16r">{currency.option?.name}</p>
          <p className="ml-auto text-base-300 typography-16r">
            {currency.option?.ticker}
          </p>
        </div>
      )}
      isClearable
      CustomSkeletons={
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-4">
            <Skeleton className="h-10 w-10 rounded-full" />
            <Skeleton className="h-[16px] w-[100px]" />
          </div>
          <Skeleton className="h-[20px] w-[50px]" />
        </div>
      }
      {...props}
    />
  );
};
